import { Link, useNavigate } from '@tanstack/react-router';
import { confirmSignIn, type SignInOutput, signIn } from 'aws-amplify/auth';
import { Loader2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { type FormEvent, useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth-state';

type Stage =
  | { kind: 'credentials' }
  | { kind: 'new-password'; email: string }
  | { kind: 'totp-code'; email: string }
  | { kind: 'totp-setup'; email: string; setupUri: string; sharedSecret: string };

export function LoginPage() {
  const navigate = useNavigate();
  const { state: authState, refresh } = useAuth();
  const [stage, setStage] = useState<Stage>({ kind: 'credentials' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authState.status === 'signed-in') {
      void navigate({ to: '/orders' });
    }
  }, [authState.status, navigate]);

  async function handleCredentials(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const email = String(form.get('email') ?? '').trim();
    const password = String(form.get('password') ?? '');
    setBusy(true);
    setError(null);
    try {
      const output = await signIn({ username: email, password });
      handleNextStep(output, email);
    } catch (err) {
      setError(messageFromError(err) ?? 'Incorrect email or password.');
    } finally {
      setBusy(false);
    }
  }

  async function handleTotp(code: string) {
    setBusy(true);
    setError(null);
    try {
      const output = await confirmSignIn({ challengeResponse: code });
      if (output.isSignedIn) {
        await refresh();
        void navigate({ to: '/orders' });
        return;
      }
      handleNextStep(output, stage.kind === 'credentials' ? '' : stage.email);
    } catch (err) {
      setError(messageFromError(err) ?? 'Invalid code. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function handleNewPassword(newPassword: string) {
    setBusy(true);
    setError(null);
    try {
      const output = await confirmSignIn({ challengeResponse: newPassword });
      if (output.isSignedIn) {
        await refresh();
        void navigate({ to: '/orders' });
        return;
      }
      handleNextStep(output, stage.kind === 'new-password' ? stage.email : '');
    } catch (err) {
      setError(messageFromError(err) ?? 'Could not set password.');
    } finally {
      setBusy(false);
    }
  }

  function handleNextStep(output: SignInOutput, email: string) {
    const step = output.nextStep;
    if (step.signInStep === 'DONE') {
      void refresh().then(() => navigate({ to: '/orders' }));
      return;
    }
    if (step.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      setStage({ kind: 'new-password', email });
      return;
    }
    if (step.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
      setStage({ kind: 'totp-code', email });
      return;
    }
    if (step.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
      const details = step.totpSetupDetails;
      setStage({
        kind: 'totp-setup',
        email,
        setupUri: details.getSetupUri('Fast Screen Print').toString(),
        sharedSecret: details.sharedSecret,
      });
      return;
    }
    setError(`Unsupported auth step: ${step.signInStep}`);
  }

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <aside
        className="relative hidden overflow-hidden bg-neutral-950 lg:flex lg:flex-col lg:justify-between lg:p-12"
        style={{
          backgroundImage:
            'radial-gradient(circle at top right, oklch(0.45 0.20 25 / 0.45), transparent 60%)',
        }}
      >
        <div className="relative flex items-center gap-4">
          <img src="/fsp-logo-white.png" alt="" className="size-16 shrink-0 object-contain" />
          <span className="text-base font-semibold uppercase tracking-wider text-white/90">
            Fast Screen Print
          </span>
        </div>
        <div className="relative space-y-3">
          <h2 className="text-3xl font-semibold leading-tight text-white">Operations console</h2>
          <p className="text-sm leading-relaxed text-white/60">Quoting · Leads · Calls · Blanks</p>
        </div>
        <p className="relative text-xs text-white/40">&copy; Fast Screen Print — McAllen, TX</p>
      </aside>

      <section className="flex items-center justify-center px-6 py-12 sm:px-10">
        <div className="w-full max-w-sm space-y-6">
          <div className="flex items-center gap-3 lg:hidden">
            <img src="/fsp-logo.png" alt="" className="size-12 shrink-0 object-contain" />
            <span className="text-base font-semibold uppercase tracking-wider">
              Fast Screen Print
            </span>
          </div>

          {stage.kind === 'credentials' && (
            <CredentialsCard onSubmit={handleCredentials} busy={busy} error={error} />
          )}
          {stage.kind === 'new-password' && (
            <NewPasswordCard
              email={stage.email}
              onSubmit={handleNewPassword}
              busy={busy}
              error={error}
            />
          )}
          {stage.kind === 'totp-code' && (
            <TotpCodeCard
              email={stage.email}
              onSubmit={handleTotp}
              busy={busy}
              error={error}
              onCancel={() => {
                setStage({ kind: 'credentials' });
                setError(null);
              }}
            />
          )}
          {stage.kind === 'totp-setup' && (
            <TotpSetupCard
              email={stage.email}
              setupUri={stage.setupUri}
              sharedSecret={stage.sharedSecret}
              onSubmit={handleTotp}
              busy={busy}
              error={error}
            />
          )}
        </div>
      </section>
    </div>
  );
}

function CredentialsCard({
  onSubmit,
  busy,
  error,
}: {
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  busy: boolean;
  error: string | null;
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
        <p className="text-sm text-muted-foreground">
          Use your Fast Screen Print email to continue.
        </p>
      </header>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            autoFocus
            disabled={busy}
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link
              to="/reset-password"
              className="text-xs text-muted-foreground underline-offset-4 hover:underline"
            >
              Forgot password?
            </Link>
          </div>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            disabled={busy}
          />
        </div>
      </div>
      <Button type="submit" className="w-full" disabled={busy}>
        {busy && <Loader2 className="animate-spin" />}
        Continue
      </Button>
    </form>
  );
}

function NewPasswordCard({
  email,
  onSubmit,
  busy,
  error,
}: {
  email: string;
  onSubmit: (newPassword: string) => void;
  busy: boolean;
  error: string | null;
}) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mismatch, setMismatch] = useState(false);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    onSubmit(newPassword);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Set a new password</h1>
        <p className="text-sm text-muted-foreground">
          Your account is using a temporary password. Choose a new one to continue
          {email && <span className="ml-1 font-medium text-foreground">({email})</span>}.
        </p>
      </header>
      {(error || mismatch) && (
        <Alert variant="destructive">
          <AlertDescription>{mismatch ? "Passwords don't match." : error}</AlertDescription>
        </Alert>
      )}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="newPassword">New password</Label>
          <Input
            id="newPassword"
            name="newPassword"
            type="password"
            autoComplete="new-password"
            minLength={12}
            required
            autoFocus
            disabled={busy}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            12+ characters, upper + lower case, number, symbol.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm new password</Label>
          <Input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            minLength={12}
            required
            disabled={busy}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>
      </div>
      <Button type="submit" className="w-full" disabled={busy}>
        {busy && <Loader2 className="animate-spin" />}
        Set password and continue
      </Button>
    </form>
  );
}

function TotpCodeCard({
  email,
  onSubmit,
  busy,
  error,
  onCancel,
}: {
  email: string;
  onSubmit: (code: string) => void;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Two-factor code</h1>
        <p className="text-sm text-muted-foreground">
          Enter the 6-digit code from your authenticator app.
          {email && <span className="ml-1 font-medium text-foreground">{email}</span>}
        </p>
      </header>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="flex justify-center">
        <InputOTP
          maxLength={6}
          value={code}
          onChange={setCode}
          disabled={busy}
          onComplete={(value) => onSubmit(value)}
          autoFocus
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
          </InputOTPGroup>
          <InputOTPSeparator />
          <InputOTPGroup>
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>
      </div>
      <div className="space-y-2">
        <Button
          type="button"
          className="w-full"
          disabled={busy || code.length !== 6}
          onClick={() => onSubmit(code)}
        >
          {busy && <Loader2 className="animate-spin" />}
          Verify
        </Button>
        <Button type="button" variant="ghost" className="w-full" onClick={onCancel} disabled={busy}>
          Use a different account
        </Button>
      </div>
    </div>
  );
}

function TotpSetupCard({
  email,
  setupUri,
  sharedSecret,
  onSubmit,
  busy,
  error,
}: {
  email: string;
  setupUri: string;
  sharedSecret: string;
  onSubmit: (code: string) => void;
  busy: boolean;
  error: string | null;
}) {
  const [code, setCode] = useState('');
  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Set up two-factor</h1>
        <p className="text-sm text-muted-foreground">
          Scan the code with Google Authenticator, 1Password, or any TOTP app, then enter the
          6-digit code to finish signing in
          {email && <span className="ml-1 font-medium text-foreground">({email})</span>}.
        </p>
      </header>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="flex flex-col items-center gap-4 rounded-lg border bg-muted/30 p-6">
        <div className="rounded-md bg-white p-3">
          <QRCodeSVG value={setupUri} size={176} />
        </div>
        <details className="w-full text-xs text-muted-foreground">
          <summary className="cursor-pointer">Can't scan? Show manual key</summary>
          <code className="mt-2 block break-all rounded bg-background p-2 font-mono text-[11px]">
            {sharedSecret}
          </code>
        </details>
      </div>
      <div className="flex justify-center">
        <InputOTP
          maxLength={6}
          value={code}
          onChange={setCode}
          disabled={busy}
          onComplete={(value) => onSubmit(value)}
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
          </InputOTPGroup>
          <InputOTPSeparator />
          <InputOTPGroup>
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>
      </div>
      <Button
        type="button"
        className="w-full"
        disabled={busy || code.length !== 6}
        onClick={() => onSubmit(code)}
      >
        {busy && <Loader2 className="animate-spin" />}
        Finish sign-in
      </Button>
    </div>
  );
}

function messageFromError(err: unknown): string | null {
  if (err && typeof err === 'object' && 'message' in err && typeof err.message === 'string') {
    return err.message;
  }
  return null;
}
